<script setup>
import { DCaret } from '@element-plus/icons-vue'
import { formatSize } from '~/utils/formater'

const props = defineProps(['productId', 'selectedColor'])

const shopAccess = useShopAccess()
const pricesVisible = usePricesVisible()

// Первый список грузим на сервере: это перелинковка между карточками,
// без неё поисковик не видит связей между товарами
const { data: initialRelated } = await useAsyncData(
    `related-${props.productId}`,
    () => $fetch('/api/related_products', {
        query: {
            product_id: props.productId,
            selected_color: props.selectedColor ?? 0,
        },
    })
)

const relatedProducts = ref(initialRelated.value ?? [])
const selectedColor = ref(props.selectedColor)

// Покупатель сменил цвет — обновляем подборку под него
watch(() => props.selectedColor, async (newValue) => {
    if (newValue === null || newValue === undefined || newValue < 0) return
    selectedColor.value = newValue
    relatedProducts.value = await $fetch('/api/related_products', {
        query: {
            product_id: props.productId,
            selected_color: newValue,
        },
    })
})

// Функция для получения URL изображения по его ID
const getImageUrl = (id, size, p) => {
    if (!id) return `/img/no_img.png`
    else if (id && !p) return `/api/image/${category_alias}/${product_alias}/color/${getImageAliasById(id)}/${id}${size ? '?size=' + size : ''}` // Предположительно у вас есть API для получения изображения по ID
    else if (id && p) return `/api/image/${p.category_alias}/${p.product_alias}/color/primary/${id}${size ? '?size=' + size : ''}` // Предположительно у вас есть API для получения изображения по ID
}

const generateAltForImage = (type, data, prefix = '', postfix = '') => {
    if (type == 'RELATE') {
        return `${prefix}Изделие "${data.product_name}", категория "${data.category_name}".`
    }
    return 'TO DO'
}

</script>

<template>
    <div>
        <h2 class="related_label" v-if="relatedProducts.length">Похожие товары</h2>
        <div class="related-products">
            <NuxtLink v-for="product in relatedProducts" :key="product.parent_product_id"
                :to="`/catalog/${product.category_alias}/${product.product_alias}${product.color_alias ? ('?color='+product.color_alias): ''}`" 
                class="related-product-card">
                <div class="related-product-image">
                    <img :src="getImageUrl(product.logo, 200, product)"
                        :alt="generateAltForImage('RELATE', product, 'Похожий товар: ', '')" width="100" height="100" />
                </div>
                <div class="related-product-info">
                    <h3>{{ product.product_name }}</h3>
                    <div class="price-size-container">
                        <span class="product-size"><el-icon style="font-size: 15px;">
                                <DCaret />
                            </el-icon>{{ formatSize(product.size_sm) }} см</span>
                        <!-- Цена видна только тем, кому доступен магазин —
                             то же правило, что в каталоге и на карточке товара -->
                        <p v-if="pricesVisible && product.price !== null">{{ product.price }} ₽</p>
                    </div>
                </div>
            </NuxtLink>
        </div>
    </div>
</template>

<style scoped>
.related_label {
    margin: 15px 0px 10px 0px;
}

/* Стили для горизонтального списка карточек */
.related-products {
    display: flex;
    flex-wrap: wrap;
    /* Позволяет элементам переноситься на новую строку */
    overflow-x: auto;
    padding: 0;
    gap: 15px;
    overflow-y: hidden;
    overflow-x: hidden;
}

.related-product-card {
    gap: 10px;
    /* Отступ между изображением и текстом */
    min-width: 270px;
    /* Минимальная ширина для карточки */
    max-width: 270px;
    /* width: 200px; */
    display: flex;
    flex-direction: row;
    /* Выстраиваем элементы в одну строку */
    align-items: center;
    /* Центрируем по вертикали */
    background-color: var(--el-bg-color);
    color: var(--el-text-color-primary);
    border: 1px solid var(--el-border-color-light);
    border-radius: 5px;
    padding: 10px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s;
    text-decoration: none;
}

.related-product-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.related-product-image {
    flex-shrink: 0;
    /* Фиксируем размер изображения */
    width: 100px;
    /* Фиксированная ширина */
    height: 100px;
    /* Фиксированная высота */
}

.related-product-image img {
    width: 100px;
    height: 100px;
    object-fit: cover;
    margin-bottom: 10px;
    background: var(--el-fill-color-light);
}

.related-product-info {
    display: flex;
    flex-direction: column;
    /* Выстраиваем элементы вертикально */
    justify-content: space-between;
    /* Разделяем заголовок и цену по вертикали */
    flex-grow: 1;
    /* Информация будет занимать всё оставшееся пространство */
    height: 100%;
    /* Убедимся, что блок растягивается по высоте */
    overflow: hidden;
    /* Для обрезки текста */
}

.related-product-info h3 {
    font-size: 14px;
    margin: 0;
    margin-bottom: 5px;
    display: -webkit-box;
    /* Для работы с multiline ellipsis */
    -webkit-box-orient: vertical;
    line-clamp: 4;
    -webkit-line-clamp: 4;
    /* Ограничиваем заголовок максимум тремя строками */
    overflow: hidden;
    /* Обрезаем текст, если он выходит за границы */
    text-overflow: ellipsis;
    /* Добавляем троеточие, если текст не помещается */
    text-align: left;
    font-weight: 400;
}

.related-product-info p {
    font-size: 14px;
    color: var(--el-text-color-primary);
    margin: 0;
    align-self: flex-end;
    /* Размещаем цену справа внизу */
    font-size: 20px;
    font-weight: 400;
    text-align: right;
}

/* Контейнер для цены и размера */
.price-size-container {
    display: flex;
    justify-content: space-between;
    /* Цена справа, размер слева */
    align-items: center;
    /* Выравнивание по вертикали */
}

.product-size {
    font-size: 20px;
    font-weight: 400;
    color: var(--el-text-color-primary);
}
</style>